import React, { useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Plus } from 'lucide-react';
import { stagger, fadeUp } from '@/lib/motion';
import { useCoverClub } from './context/CoverClubContext';
import { COLLECTION_DEFS } from './constants';
import { useModalState } from './hooks';
import CollectionCard from './CollectionCard';
import CreateCollectionModal from './CreateCollectionModal';
import CoverFeed from './CoverFeed';
import { toast } from '@/components/ui/sonner';

function AddCollectionTile({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="cover-club-create-collection"
      className="flex flex-col rounded-2xl border-2 border-dashed border-line p-4 text-left transition-colors hover:border-coral/50 hover:bg-coral/5"
    >
      <div className="flex h-32 w-full items-center justify-center">
        <span className="grid h-11 w-11 place-items-center rounded-full bg-coral-fill text-[#FFF7F0]">
          <Plus className="h-4 w-4" />
        </span>
      </div>
      <div className="mt-3">
        <p className="font-display text-base font-semibold text-coral">New Collection</p>
        <p className="mt-0.5 text-xs text-subtle">Curate your own</p>
      </div>
    </button>
  );
}

export default function CollectionsSection({ activeCollection, onOpenCollection, onOpenCover }) {
  const { entries, customCollections, loadCollections } = useCoverClub();
  const [isCreateOpen, openCreate, closeCreate] = useModalState(false);

  useEffect(() => {
    loadCollections().catch((error) => toast(error.message || 'We could not load cover collections.'));
  }, [loadCollections]);

  const allCollections = useMemo(() => [
    ...COLLECTION_DEFS.map((c) => ({ ...c, id: c.key })),
    ...customCollections,
  ], [customCollections]);

  const entriesFor = (collection) => {
    if (collection.custom) return entries.filter((e) => collection.bookIds.includes(e.bookId));
    return entries.filter((e) => e.collections?.includes(collection.key));
  };

  if (activeCollection) {
    const collection = allCollections.find((c) => (c.key ?? c.id) === activeCollection);
    if (!collection) return null;
    return (
      <div>
        <button type="button" onClick={() => onOpenCollection(null)} className="text-xs font-medium text-subtle hover:text-coral">
          ← All collections
        </button>
        <h2 className="mt-2 font-display text-2xl font-semibold text-ink">{collection.title}</h2>
        <p className="text-xs text-subtle">{collection.curator}</p>
        <div className="mt-6">
          <CoverFeed collectionFilter={collection.key ?? null} onOpenCover={onOpenCover} />
          {collection.custom && entriesFor(collection).length === 0 && (
            <p className="mt-6 text-sm text-subtle">No covers in this collection yet.</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="cover-club-collections">
      <p className="text-sm text-subtle">Editorial picks and community-curated collections.</p>

      <motion.div variants={stagger} initial="hidden" animate="show" className="mt-5 grid grid-cols-2 gap-5 sm:grid-cols-3">
        <motion.div variants={fadeUp} custom={0}>
          <AddCollectionTile onClick={openCreate} />
        </motion.div>
        {allCollections.map((collection, i) => (
          <motion.div key={collection.key ?? collection.id} variants={fadeUp} custom={i + 1}>
            <CollectionCard collection={collection} entries={entriesFor(collection)} onOpen={onOpenCollection} />
          </motion.div>
        ))}
      </motion.div>

      <CreateCollectionModal isOpen={isCreateOpen} onClose={closeCreate} />
    </div>
  );
}
